import React, { useState } from 'react'
import {
  ArrowIcon,
  EyeIcon,
  DownloadIcon,
  AylaIcon,
  DocumentIcon,
} from './icons'
import { Modal } from '~/components/_layouts/index'
import useTranslation from '../../shared/TranslationProvider'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { TemplateSwitcher } from '~/lib/pdf'
import { useTemplate } from '~/lib/pdf/TemplateProvider'
import StandaloneViewer from '~/components/ResumeBuilder/StandaloneViewer'
import CoverLetterStandaloneViewer from '~/components/CoverLetterBuilder/StandaloneViewer'
import ThankYouNoteStandaloneViewer from '~/components/ThankYouNoteBuilder/StandaloneViewer'
import NetworkingBriefStandaloneViewer from '~/components/NetworkingBriefBuilder/StandaloneViewer'

export default function Exports({ exportsData }) {
  const { t } = useTranslation('MyAccountSettings')
  const [isMinimized, setIsMinimized] = useState(false)
  const [previewDoc, setPreviewDoc] = useState(null)

  const handlePreview = (doc) => setPreviewDoc(doc)
  const handleClose = () => setPreviewDoc(null)

  const { templateIndex, templateStyle, templates } = useTemplate()

  const merge = (arr) => arr?.map((item) => ({ ...item })) || []
  const allExports = [
    ...merge(exportsData.documents),
    ...merge(exportsData.chat_histories),
    ...merge(exportsData.resumes),
    ...merge(exportsData.cover_letters),
    ...merge(exportsData.thank_you_notes),
    ...merge(exportsData.networking_briefs),
  ].sort((a, b) => new Date(b.date) - new Date(a.date))
  const hasExports = allExports.length > 0 || exportsData.coach_feedback

  const CoachFeedbackRow = ({ feedback }) => (
    <div className="flex items-center justify-between border-b border-[#b9b9b9] py-3">
      <div className="flex items-center">
        <DocumentIcon className="pr-2" />
        <div className="ml-4 flex flex-col gap-2">
          <div className="text-base">{feedback.name}</div>
        </div>
      </div>
      <div className="flex items-center gap-10">
        <a href={feedback.url} download title="Download">
          <DownloadIcon />
        </a>
      </div>
    </div>
  )

  const ExportRow = ({ item, idx, last }) => (
    <div
      key={item.url || item.name || idx}
      className={`flex items-center py-3 justify-between${!last ? ' border-b border-[#b9b9b9]' : ''}`}
    >
      <div className="flex items-center">
        {item.type.includes('ayla') ? (
          <AylaIcon className="pr-2" />
        ) : (
          <DocumentIcon className="pr-2" />
        )}
        <div className="ml-4 flex flex-col gap-2">
          <div className="text-base">{item.name}</div>
          <div className="text-sm">{item.date}</div>
        </div>
      </div>
      <div className="flex items-center gap-10">
        {!(
          item.type === 'resume_builder' &&
          (!item.document_json || Object.keys(item.document_json).length === 0)
        ) && (
          <button
            type="button"
            title={t('preview')}
            onClick={() => handlePreview(item)}
            className="cursor-pointer border-none bg-transparent p-0"
            aria-label={t('preview')}
          >
            <EyeIcon />
          </button>
        )}
        {item.document_json && Object.keys(item.document_json).length > 0 ? (
          <PDFDownloadLink
            document={
              <TemplateSwitcher
                jsonDocument={item.document_json}
                templateIndex={templateIndex}
                templateStyle={templateStyle}
                title={item.name}
                Template={templates[templateIndex]}
              />
            }
            fileName={`${item.name || 'document'}.pdf`}
          >
            <DownloadIcon />
          </PDFDownloadLink>
        ) : (
          <a
            href={item.url}
            download={`${item.name || 'document'}.pdf`}
            title={t('download')}
          >
            <DownloadIcon />
          </a>
        )}
      </div>
    </div>
  )

  return (
    <div
      className="h-fit rounded-[20px] border border-[#B9B9B9] p-4 sm:p-8"
      style={{ boxShadow: '0px 4px 4px 0px #99999930' }}
    >
      <div className="col-span-2 flex items-center justify-between">
        <h2 className="!text-xl font-normal">{t('exports_title')}</h2>
        <button
          type="button"
          aria-label={isMinimized ? t('expand') : t('minimize')}
          onClick={() => setIsMinimized((prev) => !prev)}
          className={`ml-2 rounded p-1 transition-transform duration-200 hover:bg-gray-100 ${isMinimized ? 'rotate-180' : ''}`}
        >
          <ArrowIcon />
        </button>
      </div>
      {!isMinimized && (
        <div className="mt-4 text-sm text-gray-600">
          {hasExports ? (
            <>
              {exportsData.coach_feedback && (
                <CoachFeedbackRow feedback={exportsData.coach_feedback} />
              )}
              {allExports.map((item, idx) => (
                <ExportRow
                  key={`export-${idx}`}
                  item={item}
                  idx={idx}
                  last={idx === allExports.length - 1}
                />
              ))}
            </>
          ) : (
            <div>{t('no_exports')}</div>
          )}
        </div>
      )}
      {previewDoc &&
        (previewDoc.type === 'resume_builder' &&
        Object.keys(previewDoc.document_json).length > 0 ? (
          <Modal
            open={!!previewDoc}
            onClose={handleClose}
            title={previewDoc.name}
            size="md"
          >
            <ResumePreview item={previewDoc} />
          </Modal>
        ) : previewDoc.type === 'cover_letter' ||
          previewDoc.type === 'ayla_cover_letter' ? (
          <Modal
            open={!!previewDoc}
            onClose={handleClose}
            title={previewDoc.name}
            size="md"
          >
            <CoverLetterPreview item={previewDoc} />
          </Modal>
        ) : previewDoc.type === 'thank_you_note' ||
          previewDoc.type === 'ayla_thank_you_note' ? (
          <Modal
            open={!!previewDoc}
            onClose={handleClose}
            title={previewDoc.name}
            size="md"
          >
            <ThankYouNotePreview item={previewDoc} />
          </Modal>
        ) : previewDoc.type === 'networking_brief' ? (
          <Modal
            open={!!previewDoc}
            onClose={handleClose}
            title={previewDoc.name}
            size="md"
          >
            <NetworkingBriefPreview item={previewDoc} />
          </Modal>
        ) : (
          <Modal
            open={!!previewDoc}
            onClose={handleClose}
            title={previewDoc.name}
            size="md"
          >
            <div className="p-4">
              <iframe
                src={`${previewDoc.url}#toolbar=0&navpanes=0`}
                className="min-h-96 w-full"
                title="Resume Preview"
                frameBorder="0"
                loading="lazy"
              />
            </div>
          </Modal>
        ))}
    </div>
  )
}

const ResumePreview = ({ item }) => {
  const {
    template_index,
    template_settings,
    name: resume_name,
    document_json: resume_json,
  } = item

  const props = {
    template_index,
    template_settings,
    resume_name,
    resume_json,
    HideToolBar: true,
  }

  return <StandaloneViewer {...props} />
}

const CoverLetterPreview = ({ item }) => {
  const {
    template_index,
    template_settings,
    name: cover_letter_name,
    document_json: cover_letter_json,
  } = item

  const props = {
    template_index,
    template_settings,
    cover_letter_name,
    cover_letter_json,
    HideToolBar: true,
  }

  return <CoverLetterStandaloneViewer {...props} />
}

const ThankYouNotePreview = ({ item }) => {
  const {
    template_index,
    template_settings,
    name: thank_you_note_name,
    document_json: thank_you_note_json,
  } = item

  const props = {
    template_index,
    template_settings,
    thank_you_note_name,
    thank_you_note_json,
    HideToolBar: true,
  }

  return <ThankYouNoteStandaloneViewer {...props} />
}

const NetworkingBriefPreview = ({ item }) => {
  const {
    template_index,
    template_settings,
    name: networking_brief_name,
    document_json: networking_brief_json,
  } = item

  const props = {
    template_index,
    template_settings,
    networking_brief_name,
    networking_brief_json,
    HideToolBar: true,
  }

  return <NetworkingBriefStandaloneViewer {...props} />
}
